import React, { useState, useEffect } from "react";
import { THUMB_REFRESH_SPIN_MS, THUMB_STRATEGY } from "@/lib/constants";

// Strategy order:
// 1. https://image.thum.io/get/width/1200/crop/750/viewportWidth/1440/<URL>
// 2. https://s.wordpress.com/mshots/v1/<encodedURL>?w=1200&h=750
// 3. Fallback placeholder with initial

function buildThumbioUrl(url, bust) {
  return `https://image.thum.io/get/width/1200/crop/750/viewportWidth/1440/noanimate/${url}${
    bust ? `?t=${bust}` : ""
  }`;
}

function buildMshotsUrl(url, bust) {
  return `https://s.wordpress.com/mshots/v1/${encodeURIComponent(url)}?w=1200&h=750${
    bust ? `&t=${bust}` : ""
  }`;
}

function resolveSrc(url, strategy, bust) {
  if (!url) return "";
  if (strategy === THUMB_STRATEGY.THUMBIO) return buildThumbioUrl(url, bust);
  if (strategy === THUMB_STRATEGY.MSHOTS) return buildMshotsUrl(url, bust);
  return "";
}

function getInitial(name, url) {
  return (name || url || "?").trim().charAt(0).toUpperCase();
}

export default function Thumbnail({ url, name, refreshKey = 0 }) {
  const [strategy, setStrategy] = useState(THUMB_STRATEGY.THUMBIO);
  const [spinning, setSpinning] = useState(false);
  const [bust, setBust] = useState(0);

  useEffect(() => {
    setStrategy(THUMB_STRATEGY.THUMBIO);
    setBust(0);
  }, [url, refreshKey]);

  const handleError = () => {
    setStrategy((s) =>
      s < THUMB_STRATEGY.FALLBACK ? s + 1 : THUMB_STRATEGY.FALLBACK
    );
  };

  const refresh = (e) => {
    e.stopPropagation();
    setSpinning(true);
    setStrategy(THUMB_STRATEGY.THUMBIO);
    setBust(Date.now());
    setTimeout(() => setSpinning(false), THUMB_REFRESH_SPIN_MS);
  };

  const openUrl = (e) => {
    e.stopPropagation();
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const initial = getInitial(name, url);
  const src = resolveSrc(url, strategy, bust);
  const showImage = url && strategy < THUMB_STRATEGY.FALLBACK;

  return (
    <div
      className="thumb"
      onClick={openUrl}
      role={url ? "button" : undefined}
      title={url ? "Öffnen: " + url : ""}
    >
      {showImage ? (
        <img
          key={`${strategy}-${bust}-${url}`}
          src={src}
          alt={name || "Vorschau"}
          onError={handleError}
          loading="lazy"
        />
      ) : (
        <div className="thumb-fallback">
          <span className="initial">{initial}</span>
          <small>{url ? "keine vorschau" : "keine url"}</small>
        </div>
      )}
      <div className="thumb-grad" />
      {url && (
        <button
          className={`thumb-refresh${spinning ? " spinning" : ""}`}
          onClick={refresh}
          title="Vorschau aktualisieren"
          aria-label="Vorschau aktualisieren"
          data-testid="thumb-refresh"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 12a9 9 0 0 1 9-9 9.74 9.74 0 0 1 7 3" />
            <path d="M21 4v6h-6" />
            <path d="M21 12a9 9 0 0 1-9 9 9.74 9.74 0 0 1-7-3" />
            <path d="M3 20v-6h6" />
          </svg>
        </button>
      )}
    </div>
  );
}
